import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { BottomDrawerProps } from '../../types/bottomDrawer';
import { throttle } from '../../utils/utilFunctions';
import { Button } from '../button/Button';
import ChevronIcon from '../icons/ChevronIcon';
import XIcon from '../icons/XIcon';
import Typography from '../typography/Typography';
import './bottomDrawer.scss';

const BottomDrawer: FunctionComponent<BottomDrawerProps> = ({
  isOpen,
  className = '',
  onClose,
  title,
  text,
  children,
  goBack,
  enableDesktopView,
}) => {
  const [startOffset, setStartOffset] = useState<number>(0);
  const [backdropOpacity, setBackdropOpacity] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [bottomPosition, setBottomPosition] = useState<number>(0);

  const drawerRef = useRef<HTMLDivElement>(null);

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);

    if (!startOffset) {
      setStartOffset(drawerRef.current!.clientHeight - (window.innerHeight - e.touches[0].clientY));
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const clientY = e.changedTouches[0].clientY;
    const drawerHeight = drawerRef.current!.clientHeight;
    const newPosition = -(clientY - (window.innerHeight - drawerHeight + startOffset));

    const opacity = parseFloat(((drawerHeight - Math.abs(newPosition)) / drawerHeight).toFixed(2)) / 2;

    if (newPosition <= 0 && Math.abs(newPosition) < drawerHeight) {
      setBottomPosition(newPosition);
      setBackdropOpacity(opacity);
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(false);

    const clientY = e.changedTouches[0].clientY;
    const drawerHeight = drawerRef.current!.clientHeight;
    if (window.innerHeight - clientY < drawerHeight / 2) {
      onClose!();
      setBackdropOpacity(0);
    }
    else {
      setBottomPosition(0);
      setBackdropOpacity(0.5);
    }
  };

  useEffect(() => {
    setBottomPosition(0);
    if (isOpen) {
      setBackdropOpacity(0.5);
    }
    else {
      setBackdropOpacity(0);
    }
  }, [isOpen]);

  const throttledOnTouchMove = useMemo(() => throttle(onTouchMove, 150), [onTouchMove]);

  return (
    <RemoveScroll enabled={isOpen}>
      <div
        onClick={onClose}
        className={`bottom-drawer-background ${enableDesktopView ? 'desktop-view' : ''} ${isOpen ? 'visible' : ''}`}
        style={{
          opacity: backdropOpacity,
          transition: isDragging || !isOpen ? 'none' : 'all 0.2s ease-in-out',
        }}
      />

      <div
        className={`bottom-drawer-wrapper ${enableDesktopView ? 'desktop-view' : ''} ${isOpen ? 'visible' : ''} ${className}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          bottom: bottomPosition,
          transition: isDragging ? 'none' : 'all 0.2s ease-in-out',
        }}
        ref={drawerRef}
      >
        <div
          className="top-part p-v-5"
          onTouchStart={onTouchStart}
          onTouchMove={throttledOnTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="line-wrapper d-flex justify-center m-b-6">
            <div className="line" />
          </div>
          <div className={`drawer-header d-flex align-center ${goBack ? 'justify-space-between' : 'justify-content-flex-end'}`}>
            {goBack && (
              <div className="back-button-wrapper">
                <Button
                  label="Back"
                  priority="tertiary"
                  icon={<ChevronIcon direction="left" height={15} />}
                  iconPosition="left"
                  textColor="asphalt6"
                  onClick={goBack}
                  className="back-button"
                />
              </div>
            )}
            <XIcon width={27} onClick={onClose} className="close-icon" />
          </div>
        </div>
        <div data-testid="bottomDrawer-title" className="header-text">
          <Typography variant="h5">
            {title}
          </Typography>
        </div>
        <div className="content m-t-20" style={{ maxHeight: `calc(${window.innerHeight}px - 158px)` }}>
          <Typography data-testid="bottomDrawer_content-title" variant="h5" className="content-title title">
            {title}
          </Typography>
          {text && (
          <Typography data-testid="bottomDrawer_content-text" variant="body1" className="text">
            {text}
          </Typography>
          )}
          {children}
        </div>
      </div>
    </RemoveScroll>
  );
};

export default BottomDrawer;
